import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  SegmentedControl,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@nexus/ui";

import { useSuggestPersonaScripts } from "@/lib/queries";

import type { HubScriptSetPickerProps } from "../../shared/HubScriptSetPicker/HubScriptSetPicker.js";
import { HubScriptSetPicker } from "../../shared/HubScriptSetPicker/HubScriptSetPicker.js";
import type { PersonaOpportunitySetType } from "../../../types/persona-opportunities.types.js";
import {
  GenerateScriptsModal,
  type GenerateScriptsTarget,
} from "../generate-scripts/GenerateScriptsModal.js";
import { PersonaImportScriptModal, type ImportKind } from "../import-script/PersonaImportScriptModal.js";
import { PersonaSuggestScriptModal } from "../tabs/ScriptsTab/PersonaSuggestScriptModal.js";

type GenerateTab = "idea" | "import";

export interface PersonaGenerateScriptModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** Custom-set flow. Opportunity flow ignores this and uses `targets`. */
  mode: "custom" | "opportunities";
  setId: string;
  /** Legacy Opportunities sets cannot start a brief from this dialog. */
  setType?: PersonaOpportunitySetType;
  setPicker?: HubScriptSetPickerProps;
  suspendDismiss?: boolean;
  targets?: GenerateScriptsTarget[];
  onOpportunityAccepted?: () => void;
  initialTab?: GenerateTab;
}

function keepDialogMounted(suspend: boolean | undefined) {
  return (event: { preventDefault: () => void }) => {
    if (suspend) event.preventDefault();
  };
}

export function PersonaGenerateScriptModal({
  open,
  onClose,
  tenantId,
  mode,
  setId,
  setType,
  setPicker,
  suspendDismiss = false,
  targets = [],
  onOpportunityAccepted,
  initialTab = "idea",
}: PersonaGenerateScriptModalProps) {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<GenerateTab>("idea");
  const [kind, setKind] = React.useState<ImportKind>("script");
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const suggestScripts = useSuggestPersonaScripts({
    onAccepted: () => onCloseRef.current(),
  });
  // Pending lasts until the worker finishes, so it must not block the next
  // custom script or brief. One open shares one idempotency key while the
  // YouTube format stays the same (a double click or a retry is one job).
  // Changing the format mints a new key. Reopening mints a new key so the
  // next run is not a retry.
  const suggestAttemptRef = React.useRef(false);
  const suggestIdempotencyKeyRef = React.useRef<string | null>(null);
  const suggestFormatRef = React.useRef<string | undefined>(undefined);
  const [attemptLocked, setAttemptLocked] = React.useState(false);
  const pickedSetType = setPicker?.sets.find((set) => set.id === setId)?.type;
  const activeSetType = pickedSetType ?? setType;
  const isCompetitorSet = activeSetType === "COMPETITOR";

  const resetSuggestScripts = suggestScripts.reset;
  const suggestFailed = suggestScripts.isError;
  const wasOpen = React.useRef(false);

  React.useEffect(() => {
    if (!open) return;
    // Import posts to an endpoint that rejects COMPETITOR sets. Opening on that
    // tab, or switching the picker onto one, would only produce a 400.
    setTab(isCompetitorSet ? "idea" : initialTab);
    setKind("script");
  }, [open, initialTab, isCompetitorSet]);

  React.useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return;
    suggestAttemptRef.current = false;
    suggestIdempotencyKeyRef.current = null;
    suggestFormatRef.current = undefined;
    setAttemptLocked(false);
    if (suggestFailed) resetSuggestScripts();
  }, [open, resetSuggestScripts, suggestFailed]);

  React.useEffect(() => {
    if (!suggestFailed) return;
    suggestAttemptRef.current = false;
    setAttemptLocked(false);
  }, [suggestFailed]);

  React.useEffect(() => {
    if (isCompetitorSet) setKind("script");
  }, [isCompetitorSet]);

  if (mode === "opportunities") {
    return (
      <GenerateScriptsModal
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        tenantId={tenantId}
        targets={targets}
        onAccepted={() => {
          onOpportunityAccepted?.();
          onClose();
        }}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && suspendDismiss) return;
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="top-16 max-h-[calc(100dvh-5rem)] translate-y-0 gap-0 overflow-y-auto p-0 sm:top-20 sm:max-h-[calc(100dvh-6rem)] sm:max-w-2xl"
        aria-describedby={undefined}
        data-testid="persona-generate-script-modal"
        onInteractOutside={keepDialogMounted(suspendDismiss)}
        onPointerDownOutside={keepDialogMounted(suspendDismiss)}
        onFocusOutside={keepDialogMounted(suspendDismiss)}
        onEscapeKeyDown={keepDialogMounted(suspendDismiss)}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border/60 py-5 pr-14 pl-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--ai-fill-chip)]">
              <Icon name="sparkles" size="sm" className="text-[var(--ai-text)]" />
            </div>
            <DialogTitle className="text-base font-semibold leading-snug">
              {t("personaOpportunities.hub.actions.createScript", "Custom Script")}
            </DialogTitle>
          </div>
          <SegmentedControl
            value={kind}
            accent="primary"
            tone="subtle"
            className="shrink-0"
            ariaLabel={t("personaOpportunities.scripts.import.kindAria", "What to import")}
            onValueChange={(value) => {
              if (value === "brief" && isCompetitorSet) return;
              if (value === "script" || value === "brief") setKind(value);
            }}
            options={[
              { value: "script", label: t("personaOpportunities.scripts.import.kindScript", "Script") },
              {
                value: "brief",
                label: t("personaOpportunities.scripts.import.kindBrief", "Brief"),
                disabled: isCompetitorSet,
                title: isCompetitorSet
                  ? t(
                      "personaOpportunities.scripts.import.briefDisabledCompetitor",
                      "Competitor sets don't generate briefs.",
                    )
                  : undefined,
              },
            ]}
          />
        </div>
        <Tabs
          value={isCompetitorSet ? "idea" : tab}
          onValueChange={(value) => {
            if (isCompetitorSet) return;
            setTab(value as GenerateTab);
          }}
          className="gap-0"
        >
          <TabsList variant="line" className="px-6">
            <TabsTrigger value="idea" data-testid="generate-script-tab-idea">
              {t("personaOpportunities.scripts.generate.tabIdea", "Generate from my idea")}
            </TabsTrigger>
            {isCompetitorSet ? null : (
              <TabsTrigger value="import" data-testid="generate-script-tab-import">
                {t("personaOpportunities.scripts.generate.tabImport", "Import my own")}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        {setPicker ? <HubScriptSetPicker {...setPicker} /> : null}
        {tab === "idea" || isCompetitorSet ? (
          <PersonaSuggestScriptModal
            embedded
            open={open}
            onClose={onClose}
            outputKind={kind}
            setId={setId}
            error={suggestScripts.error ? String(suggestScripts.error.message) : null}
            isSubmitting={attemptLocked}
            onSubmit={(description, variations, referenceVideoUrl, youtubeFormat) => {
              if (suggestAttemptRef.current) return;
              suggestAttemptRef.current = true;
              setAttemptLocked(true);
              if (
                suggestIdempotencyKeyRef.current == null ||
                suggestFormatRef.current !== youtubeFormat
              ) {
                suggestIdempotencyKeyRef.current = crypto.randomUUID();
              }
              suggestFormatRef.current = youtubeFormat;
              suggestScripts.mutate({
                setId,
                tenantId,
                description,
                variations,
                ...(referenceVideoUrl ? { referenceVideoUrl } : {}),
                outputType: kind,
                ...(youtubeFormat ? { youtubeFormat } : {}),
                idempotencyKey: suggestIdempotencyKeyRef.current,
              });
            }}
          />
        ) : (
          <PersonaImportScriptModal
            embedded
            open={open}
            onOpenChange={(next) => {
              if (!next) onClose();
            }}
            kind={kind}
            onKindChange={setKind}
            setId={setId}
            tenantId={tenantId}
            suspendDismiss={suspendDismiss}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
